// Sicherheitskonzept.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import { QRCodeCanvas } from 'qrcode.react';

const Sicherheitskonzept = ({ show, onClose, lager }) => {
  const [sikoData, setSikoData] = useState({
    lagerForm: '',
    lagerAdresse: '',
    coordinates: '',
    notUnterkunft: '',
    naechstesSpital: '',
    naechsterArzt: '',
    teilnehmerinnen: '',
    teilnehmer: '',
    hauptLagerleitung: {
      vorname: '',
      nachname: '',
      pfadiname: '',
      telefonnummer: '',
    },
    leitungsteam: [],
    kueche: [],
    als: [],
    coach: { vorname: '', nachname: '', pfadiname: '', telefonnummer: '' },
    lagerpakt: {},
    umgebung: {},
    suchtmittel: {},
    hygiene: {},
    intim: {},
    feuer: {},
    werkzeug: {},
    sackmesser: {},
    zecken: {},
    sikoUmgang: {},
    apotheke: {},
    notfall: {},
    alarmierung: {},
    notfallauto: false,
    zuständigApotheke: '',
  });

  // Beim Öffnen des Modals (show === true) werden alle Werte aus lager übernommen,
  // sofern vorhanden – so dass das Formular editierbar wird.
  useEffect(() => {
    if (show && lager) {
      setSikoData({
        lagerForm: lager.lagerForm || '',
        lagerAdresse: lager.lagerAdresse || '',
        coordinates: lager.coordinates || '',
        notUnterkunft: lager.notUnterkunft || '',
        naechstesSpital: lager.naechstesSpital || '',
        naechsterArzt: lager.naechsterArzt || '',
        teilnehmerinnen: lager.teilnehmerinnen || '',
        teilnehmer: lager.teilnehmer || '',
        hauptLagerleitung: {
          vorname: lager.hauptLagerleitung?.vorname || '',
          nachname: lager.hauptLagerleitung?.nachname || '',
          pfadiname: lager.hauptLagerleitung?.pfadiname || '',
          telefonnummer: lager.hauptLagerleitung?.telefonnummer || '',
        },
        leitungsteam: lager.leitungsteam || [],
        kueche: lager.kueche || [],
        als: lager.als || [],
        coach: lager.coach || { vorname: '', nachname: '', pfadiname: '', telefonnummer: '' },
        lagerpakt: lager.lagerpakt || {},
        umgebung: lager.umgebung || {},
        suchtmittel: lager.suchtmittel || {},
        hygiene: lager.hygiene || {},
        intim: lager.intim || {},
        feuer: lager.feuer || {},
        werkzeug: lager.werkzeug || {},
        sackmesser: lager.sackmesser || {},
        zecken: lager.zecken || {},
        sikoUmgang: lager.sikoUmgang || {},
        apotheke: lager.apotheke || {},
        notfall: lager.notfall || {},
        alarmierung: lager.alarmierung || {},
        notfallauto: lager.notfallauto || false,
        zuständigApotheke: lager.zuständigApotheke || '',
      });
    }
  }, [show, lager]);

  // Hilfsfunktionen zur Aktualisierung der Formularwerte:
  const handleCheckboxChange = (section, key) => {
    setSikoData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: !prev[section][key],
      },
    }));
  };

  const handleInputChange = (field, value) => {
    setSikoData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setSikoData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Funktionen für dynamische Teameinträge
  const addTeamMember = (teamKey) => {
    setSikoData(prev => ({
      ...prev,
      [teamKey]: [...prev[teamKey], { vorname: '', nachname: '', pfadiname: '', telefonnummer: '' }],
    }));
  };

  const updateTeamMember = (teamKey, index, field, value) => {
    const updatedTeam = [...sikoData[teamKey]];
    updatedTeam[index][field] = value;
    setSikoData(prev => ({
      ...prev,
      [teamKey]: updatedTeam,
    }));
  };

  const removeTeamMember = (teamKey, index) => {
    const updatedTeam = sikoData[teamKey].filter((_, i) => i !== index);
    setSikoData(prev => ({
      ...prev,
      [teamKey]: updatedTeam,
    }));
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    let y = 10;
    doc.setFontSize(16);
    doc.text(`Sicherheitskonzept: ${lager.name}`, 50, 20);
    doc.setFontSize(12);
    doc.text(`Startdatum: ${new Date(lager.startDate).toLocaleDateString()}`, 10, 40);
    doc.text(`Enddatum: ${new Date(lager.endDate).toLocaleDateString()}`, 10, 50);
    doc.text(
      `Hauptlagerleitung: ${lager.hauptLagerleitung ? lager.hauptLagerleitung.vorname + ' ' + lager.hauptLagerleitung.nachname : ''}`,
      10,
      60
    );
    y = 70;
    doc.text('Informationen zum Lager:', 10, y);
    y += 10;
    doc.text(`Lagerform: ${sikoData.lagerForm}`, 10, y);
    y += 10;
    doc.text(`Lageradresse: ${sikoData.lagerAdresse}`, 10, y);
    y += 10;
    doc.text(`Koordinaten: ${sikoData.coordinates}`, 10, y);
    y += 10;
    if (sikoData.lagerForm === 'Zeltlager') {
      doc.text(`Adresse Notunterkunft: ${sikoData.notUnterkunft}`, 10, y);
      y += 10;
    }
    doc.text(`Naechstes Spital: ${sikoData.naechstesSpital}`, 10, y);
    y += 10;
    doc.text(`Naechster Arzt: ${sikoData.naechsterArzt}`, 10, y);
    y += 10;
    doc.text(`Teilnehmerinnen: ${sikoData.teilnehmerinnen}`, 10, y);
    y += 10;
    doc.text(`Teilnehmer: ${sikoData.teilnehmer}`, 10, y);
    y += 20;
    doc.text('Allgemeine Sicherheitsvorkehrungen:', 10, y);
    y += 10;
    const printSection = (sectionData, title) => {
      doc.text(title, 10, y);
      y += 10;
      for (const key in sectionData) {
        if (sectionData[key]) {
          doc.text(`- ${key}`, 10, y);
          y += 10;
        }
      }
      y += 5;
    };
    printSection(sikoData.lagerpakt, 'Lagerpakt');
    printSection(sikoData.umgebung, 'Umgebung');
    printSection(sikoData.suchtmittel, 'Umgang mit Suchtmitteln');
    printSection(sikoData.hygiene, 'Hygiene');
    printSection(sikoData.intim, 'Massnahmen zur Wahrung der Intimsphaere');
    printSection(sikoData.feuer, 'Umgang mit Feuer');
    printSection(sikoData.werkzeug, 'Umgang mit Werkzeug');
    printSection(sikoData.sackmesser, 'Sackmesser');
    printSection(sikoData.zecken, 'Zecken');
    printSection(sikoData.sikoUmgang, 'Umgang mit dem Sicherheitskonzept');
    printSection(sikoData.apotheke, 'Apotheke');
    printSection(sikoData.notfall, 'Handeln im Notfall');
    printSection(sikoData.alarmierung, 'Alarmierung');
    doc.text(`Notfallauto vorhanden: ${sikoData.notfallauto ? 'Ja' : 'Nein'}`, 10, y);
    y += 10;
    doc.text(`Zustaendig fuer Apotheke: ${sikoData.zuständigApotheke}`, 10, y);
    doc.save(`${lager.name}_Sicherheitskonzept.pdf`);
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/logo.png" alt="Logo" style={{ height: '40px', marginRight: '10px' }} />
          <Modal.Title>Sicherheitskonzept: {lager?.name}</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <Form>
          <h5>Informationen zum Lager</h5>
          <Form.Group className="mb-3">
            <Form.Label>Lagerform</Form.Label>
            <Form.Control
              as="select"
              value={sikoData.lagerForm}
              onChange={(e) => handleInputChange('lagerForm', e.target.value)}
            >
              <option value="">Bitte auswählen</option>
              <option value="Hauslager">Hauslager</option>
              <option value="Zeltlager">Zeltlager</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Lageradresse</Form.Label>
            <Form.Control
              type="text"
              value={sikoData.lagerAdresse}
              onChange={(e) => handleInputChange('lagerAdresse', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Koordinaten</Form.Label>
            <Form.Control
              type="text"
              value={sikoData.coordinates}
              onChange={(e) => handleInputChange('coordinates', e.target.value)}
            />
          </Form.Group>
          {sikoData.lagerForm === 'Zeltlager' && (
            <Form.Group className="mb-3">
              <Form.Label>Adresse Notunterkunft</Form.Label>
              <Form.Control
                type="text"
                value={sikoData.notUnterkunft}
                onChange={(e) => handleInputChange('notUnterkunft', e.target.value)}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Naechstes Spital</Form.Label>
            <Form.Control
              type="text"
              value={sikoData.naechstesSpital}
              onChange={(e) => handleInputChange('naechstesSpital', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Naechster Arzt</Form.Label>
            <Form.Control
              type="text"
              value={sikoData.naechsterArzt}
              onChange={(e) => handleInputChange('naechsterArzt', e.target.value)}
            />
          </Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Teilnehmerinnen</Form.Label>
                <Form.Control
                  type="number"
                  value={sikoData.teilnehmerinnen}
                  onChange={(e) => handleInputChange('teilnehmerinnen', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Teilnehmer</Form.Label>
                <Form.Control
                  type="number"
                  value={sikoData.teilnehmer}
                  onChange={(e) => handleInputChange('teilnehmer', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <h6>Hauptlagerleitung</h6>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Vorname</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.hauptLagerleitung.vorname}
                  onChange={(e) =>
                    handleNestedInputChange('hauptLagerleitung', 'vorname', e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Nachname</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.hauptLagerleitung.nachname}
                  onChange={(e) =>
                    handleNestedInputChange('hauptLagerleitung', 'nachname', e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Pfadiname</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.hauptLagerleitung.pfadiname}
                  onChange={(e) =>
                    handleNestedInputChange('hauptLagerleitung', 'pfadiname', e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Telefonnummer</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.hauptLagerleitung.telefonnummer}
                  onChange={(e) =>
                    handleNestedInputChange('hauptLagerleitung', 'telefonnummer', e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <h6>Leitungsteam</h6>
          {sikoData.leitungsteam.map((member, index) => (
            <Row key={index} className="mb-2">
              <Col>
                <Form.Control
                  placeholder="Vorname"
                  value={member.vorname}
                  onChange={(e) => updateTeamMember('leitungsteam', index, 'vorname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Nachname"
                  value={member.nachname}
                  onChange={(e) => updateTeamMember('leitungsteam', index, 'nachname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Pfadiname"
                  value={member.pfadiname}
                  onChange={(e) => updateTeamMember('leitungsteam', index, 'pfadiname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Telefonnummer"
                  value={member.telefonnummer}
                  onChange={(e) => updateTeamMember('leitungsteam', index, 'telefonnummer', e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="danger" onClick={() => removeTeamMember('leitungsteam', index)}>
                  X
                </Button>
              </Col>
            </Row>
          ))}
          <Button variant="secondary" onClick={() => addTeamMember('leitungsteam')}>
            + Leitungsteam hinzufügen
          </Button>
          <h6>Kueche</h6>
          {sikoData.kueche.map((member, index) => (
            <Row key={index} className="mb-2">
              <Col>
                <Form.Control
                  placeholder="Vorname"
                  value={member.vorname}
                  onChange={(e) => updateTeamMember('kueche', index, 'vorname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Nachname"
                  value={member.nachname}
                  onChange={(e) => updateTeamMember('kueche', index, 'nachname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Pfadiname"
                  value={member.pfadiname}
                  onChange={(e) => updateTeamMember('kueche', index, 'pfadiname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Telefonnummer"
                  value={member.telefonnummer}
                  onChange={(e) => updateTeamMember('kueche', index, 'telefonnummer', e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="danger" onClick={() => removeTeamMember('kueche', index)}>
                  X
                </Button>
              </Col>
            </Row>
          ))}
          <Button variant="secondary" onClick={() => addTeamMember('kueche')}>
            + Kueche hinzufügen
          </Button>
          <h6>AL's</h6>
          {sikoData.als.map((member, index) => (
            <Row key={index} className="mb-2">
              <Col>
                <Form.Control
                  placeholder="Vorname"
                  value={member.vorname}
                  onChange={(e) => updateTeamMember('als', index, 'vorname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Nachname"
                  value={member.nachname}
                  onChange={(e) => updateTeamMember('als', index, 'nachname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Pfadiname"
                  value={member.pfadiname}
                  onChange={(e) => updateTeamMember('als', index, 'pfadiname', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  placeholder="Telefonnummer"
                  value={member.telefonnummer}
                  onChange={(e) => updateTeamMember('als', index, 'telefonnummer', e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="danger" onClick={() => removeTeamMember('als', index)}>
                  X
                </Button>
              </Col>
            </Row>
          ))}
          <Button variant="secondary" onClick={() => addTeamMember('als')}>
            + AL's hinzufügen
          </Button>
          <h6>Coach</h6>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Vorname</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.coach.vorname}
                  onChange={(e) => handleNestedInputChange('coach', 'vorname', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Nachname</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.coach.nachname}
                  onChange={(e) => handleNestedInputChange('coach', 'nachname', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Pfadiname</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.coach.pfadiname}
                  onChange={(e) => handleNestedInputChange('coach', 'pfadiname', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Telefonnummer</Form.Label>
                <Form.Control
                  type="text"
                  value={sikoData.coach.telefonnummer}
                  onChange={(e) => handleNestedInputChange('coach', 'telefonnummer', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <h5>Allgemeine Sicherheitsvorkehrungen</h5>
          <h6>Lagerpakt</h6>
          {[
            { key: 'stopp', label: 'Stopp heisst Stopp.' },
            { key: 'platzNichtVerlassen', label: 'Der Lagerplatz darf nicht verlassen werden.' },
            { key: 'platzMitEinwilligungVerlassen', label: 'Der Lagerplatz darf mit Einwilligung der Leitenden verlassen werden.' },
            { key: 'leitendeInformieren', label: 'Beim Verlassen des Lagerplatzes müssen die Leitenden informiert werden.' },
            { key: 'fremdeZelteNachAnfrage', label: 'Fremde Zelte dürfen nur nach Anfrage betreten werden.' },
            { key: 'fremdeSchlaegeNachAnklopfen', label: 'Fremde Schlaege dürfen nur nach Anklopfen betreten werden.' },
            { key: 'fremdeZelteNichtBetreten', label: 'Fremde Zelte dürfen nicht betreten werden.' },
            { key: 'fremdeSchlaegeNichtBetreten', label: 'Fremde Schlaege dürfen nicht betreten werden.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.lagerpakt[rule.key] || false}
                onChange={() => handleCheckboxChange('lagerpakt', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Umgebung</h6>
          {[
            { key: 'trotzAufsichtGewaesser', label: 'Die TNs dürfen nur unter Aufsicht von Leitenden an Gewaesser.' },
            { key: 'trotzAufsichtFluesse', label: 'Die TNs dürfen nur unter Aufsicht von Leitenden an Fluesse.' },
            { key: 'abhangNichtNaehren', label: 'In der Naehe des Lagers befindet sich ein steiler Abhang. Diesem duerfen sich die TNs nicht naehern.' },
            { key: 'abhangAufmerksamkeit', label: 'In der Naehe des Lagers befindet sich ein steiler Abhang. Die TNs werden auf diese Gefahr aufmerksam gemacht.' },
            { key: 'strasseNichtNaehren', label: 'In der Naehe des Lagers befindet sich eine schnellbefahrene Strasse. Diesen duerfen sich die TNs nicht naehern.' },
            { key: 'strasseAufmerksamkeit', label: 'In der Naehe des Lagers befindet sich eine schnellbefahrene Strasse. Die TNs werden auf diese Gefahr aufmerksam gemacht.' },
            { key: 'steinschlagNichtBetreten', label: 'In der Naehe des Lagers befindet sich die Gefahr eines Steinschlags. Die TNs duerfen die Gefahrenzone nicht betreten.' },
            { key: 'steinschlagAufmerksamkeit', label: 'In der Naehe des Lagers befindet sich die Gefahr eines Steinschlags. Die TNs werden auf diese Gefahr aufmerksam gemacht.' },
            { key: 'hochwasser', label: 'Das Lager befindet sich in einem Hochwasser-Risikogebiet. Das Leitungsteam entscheidet ueber eine Evakuierung des Lagers.' },
            { key: 'lawinen', label: 'Das Lager befindet sich in einem Lawinen-Risikogebiet. Das Leitungsteam entscheidet ueber eine Evakuierung des Lagers.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.umgebung[rule.key] || false}
                onChange={() => handleCheckboxChange('umgebung', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Umgang mit Suchtmitteln</h6>
          {[
            { key: 'drogenVerboten', label: 'Jegliche illegale Drogen sind im Lager verboten.' },
            { key: 'alkoholVerbotTN', label: 'Der Konsum von Alkohol ist fuer alle TNs verboten.' },
            { key: 'alkoholErlaubtTN', label: 'Der Konsum von Alkohol ist fuer die TNs erlaubt, sofern die Sicherheit weiterhin gewaehrleistet werden kann.' },
            { key: 'alkoholVerbotLeit', label: 'Der Konsum von Alkohol ist fuer alle Leitenden verboten.' },
            { key: 'alkoholErlaubtLeit', label: 'Der Konsum von Alkohol ist fuer die Leitenden erlaubt, sofern die Sicherheit weiterhin gewaehrleistet werden kann und mindestens 2 Leitende nuchtern bleiben.' },
            { key: 'rauchenVerbotTN', label: 'Fuer alle TNs ist das Rauchen verboten.' },
            { key: 'rauchenErlaubtTN', label: 'Fuer die TNs ist das Rauchen unter Einwilligung der Erziehungsberechtigten an bestimmten Raucherplaetzen erlaubt.' },
            { key: 'rauchenErlaubtTN2', label: 'Fuer die TNs ist das Rauchen an bestimmten Raucherplaetzen erlaubt.' },
            { key: 'rauchenVerbotLeit', label: 'Fuer alle Leitenden ist das Rauchen verboten.' },
            { key: 'rauchenErlaubtLeit', label: 'Fuer die Leitenden ist das Rauchen an bestimmten Raucherplaetzen erlaubt.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.suchtmittel[rule.key] || false}
                onChange={() => handleCheckboxChange('suchtmittel', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Hygiene</h6>
          {[
            { key: 'haendeMitSeife', label: 'Es besteht die Moeglichkeit die Haende mit Seife zu waschen.' },
            { key: 'vorDemEssen', label: 'Vor dem Essen waschen sich alle die Haende mit Seife.' },
            { key: 'nachToilette', label: 'Nach einem Toilettengang muss man sich die Haende mit Seife waschen.' },
            { key: 'kuecheNichtBetreten', label: 'Die TNs duerfen die Kueche nicht betreten, ausser sie werden zwecks einer Aufgabe dazu aufgefordert.' },
            { key: 'kuecheNichtBetreten2', label: 'Die TNs duerfen die Kueche nicht betreten, ausser sie werden dazu aufgefordert.' },
            { key: 'zaehnePutzen', label: 'Die TNs muessen regelmaessig ihre Zaehne putzen.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.hygiene[rule.key] || false}
                onChange={() => handleCheckboxChange('hygiene', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Massnahmen zur Wahrung der Intimsphaere</h6>
          {[
            { key: 'zelterGetrennt', label: 'Die Zelter werden nach Geschlechtern getrennt.' },
            { key: 'schlaegeGetrennt', label: 'Die Schlaege werden nach Geschlechtern getrennt.' },
            { key: 'zeckenGleichesGeschlecht', label: 'Das Entfernen von Zecken in intimen Zonen muss durch eine Leitperson des gleichen Geschlechts geschehen.' },
            { key: 'mindestensZweiLeitende', label: 'Beim Entfernen von Zecken in intimen Zonen muessen mindestens zwei Leitpersonen anwesend sein.' },
            { key: 'zeckenArzt', label: 'Das Entfernen von Zecken in intimen Zonen muss durch einen Arzt geschehen.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.intim[rule.key] || false}
                onChange={() => handleCheckboxChange('intim', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Umgang mit Feuer</h6>
          {[
            { key: 'feuerTN', label: 'TNs duerfen keine Feuer entfachen.' },
            { key: 'feuerTNAufsicht', label: 'TNs duerfen unter Aufsicht einer Leitperson ein Feuer entfachen.' },
            { key: 'feuerOffiziell', label: 'Feuer duerfen nur in Feuerschalen oder offiziellen Feuerstellen entfacht werden.' },
            { key: 'feuerstellenAuswahl', label: 'Feuerstellen muessen von den Leitenden in Anbetracht des Brandschutzes gewaehlt werden.' },
            { key: 'seitlicherAbstand', label: 'Beim Anlegen einer Feuerstelle muss ein seitlicher Abstand von mindestens 3m zu Baeumen eingehalten werden.' },
            { key: 'hoehererAbstand', label: 'Beim Anlegen einer Feuerstelle muss ein Abstand von mindestens 5m zu Aesten eingehalten werden.' },
            { key: 'feuerLoeschen', label: 'Wenn ein Feuer nicht mehr gebraucht wird, muss es geloescht werden.' },
            { key: 'feuerLeitperson', label: 'Eine Leitperson muss sicherstellen, dass das Feuer richtig geloescht wurde.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.feuer[rule.key] || false}
                onChange={() => handleCheckboxChange('feuer', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Umgang mit Werkzeug</h6>
          {[
            { key: 'werkzeugeAufsicht', label: 'Die TNs duerfen Werkzeuge nur unter Aufsicht einer Leitperson verwenden.' },
            { key: 'werkzeugeVerboten', label: 'Die TNs duerfen keine Werkzeuge verwenden.' },
            { key: 'werkzeugeErlaubnis', label: 'Die TNs muessen vor dem Verwenden von Werkzeugen die Erlaubnis der Leitenden einholen.' },
            { key: 'werkzeugeNichtZweckentfremden', label: 'Werkzeuge duerfen nicht zweckentfremdet werden.' },
            { key: 'wanderschuhe', label: 'Beim Verwenden von Werkzeugen muessen Wanderschuhe getragen werden.' },
            { key: 'langeHosen', label: 'Beim Verwenden von Werkzeugen muessen lange Hosen getragen werden.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.werkzeug[rule.key] || false}
                onChange={() => handleCheckboxChange('werkzeug', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Sackmesser</h6>
          {[
            { key: 'sackmesserVerbotenTN', label: 'Die TNs duerfen keine Sackmesser verwenden.' },
            { key: 'sackmesserAufsichtTN', label: 'Die TNs duerfen Sackmesser nur unter Aufsicht der Leitenden verwenden.' },
            { key: 'sackmesserErlaubnisTN', label: 'Die TNs duerfen Sackmesser nur nach eingeholter Erlaubnis der Leitenden verwenden.' },
            { key: 'sackmesserNichtSpielen', label: 'Mit Sackmessern darf nicht gespielt werden.' },
            { key: 'dolcheAlsSackmesser', label: 'Dolche gelten auch als Sackmesser.' },
            { key: 'sackmesserNichtRichtung', label: 'Es darf nicht in die Richtung einer Person geschnitzt werden.' },
            { key: 'sackmesserImStehen', label: 'Sackmesser duerfen nur nach eingeholter Erlaubnis der Leitenden im Stehen benutzt werden.' },
            { key: 'sackmesserNurSitzen', label: 'Sackmesser duerfen generell nur im Sitzen verwendet werden.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.sackmesser[rule.key] || false}
                onChange={() => handleCheckboxChange('sackmesser', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Zecken</h6>
          {[
            { key: 'regelmaessigAbsuchen', label: 'Die TNs werden regelmaessig daran erinnert, sich nach Zecken abzusuchen.' },
            { key: 'zeckenspray', label: 'Die TNs werden mit Zeckenspray eingesprueht, wenn ein erhoehtes Risiko besteht.' },
            { key: 'zeckenNurLeitende', label: 'Zecken duerfen nur von Leitenden entfernt werden.' },
            { key: 'elternInformieren', label: 'Nach dem Entfernen einer Zecke muessen die Eltern informiert werden.' },
            { key: 'bissMarkieren', label: 'Nach dem Entfernen einer Zecke muss die Stelle des Bisses markiert werden.' },
            { key: 'symptomeBeobachten', label: 'Nach dem Entfernen einer Zecke muss beobachtet werden, ob die Person Symptome entwickelt.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.zecken[rule.key] || false}
                onChange={() => handleCheckboxChange('zecken', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Umgang mit dem Sicherheitskonzept</h6>
          {[
            { key: 'ausdruckUndBereitstellung', label: 'Das Siko wird waehrend des Lagers ausgedruckt den Leitenden zur Verfuegung stehen.' },
            { key: 'inhaltBesprechenLeitteam', label: 'Der Inhalt des Sikos wird im Leitungsteam besprochen.' },
            { key: 'inhaltBesprechenKuechenteam', label: 'Der Inhalt des Sikos wird mit dem Kuechenteam besprochen.' },
            { key: 'inhaltBesprechenTNs', label: 'Der gesamte Inhalt des Sikos wird mit den TNs besprochen.' },
            { key: 'regelnBesprechenTNs', label: 'Regeln, die aus dem Siko hervorgehen, werden mit den TNs besprochen.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.sikoUmgang[rule.key] || false}
                onChange={() => handleCheckboxChange('sikoUmgang', rule.key)}
              />
            </Form.Group>
          ))}
          <h6>Apotheke</h6>
          {[
            { key: 'apothekeLagerplatz', label: 'Die Apotheke befindet sich beim Lagerplatz.' },
            { key: 'apothekeLagerhaus', label: 'Die Apotheke befindet sich beim Lagerhaus.' },
            { key: 'apothekeMitnehmen', label: 'Bei Aktivitäten, die sich vom Lagerort entfernen, muss eine kleinere Apotheke mitgenommen werden.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.apotheke[rule.key] || false}
                onChange={() => handleCheckboxChange('apotheke', rule.key)}
              />
            </Form.Group>
          ))}
          <Form.Group className="mb-3">
            <Form.Label>Zustaendig fuer Apotheke</Form.Label>
            <Form.Control
              type="text"
              value={sikoData.zuständigApotheke}
              onChange={(e) => handleInputChange('zuständigApotheke', e.target.value)}
            />
          </Form.Group>

          <h5>Handeln im Notfall</h5>
          {[
            { key: 'isolationTN', label: 'Im Notfall werden die TNs durch eine Leitperson vom Unfall isoliert.' },
            { key: 'schemaHandeln', label: 'Im Notfall wird nach dem Schema Schauen, Denken, Handeln gearbeitet.' },
            { key: 'leitpersonMitKenntnissen', label: 'Im Notfall uebernimmt die Leitperson mit den meisten Kenntnissen den Lead.' },
            { key: 'bestimmtePersonLead', label: 'Im Notfall uebernimmt eine bestimmte Person den Lead.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.notfall[rule.key] || false}
                onChange={() => handleCheckboxChange('notfall', rule.key)}
              />
            </Form.Group>
          ))}

          <h5>Alarmierung</h5>
          {[
            { key: 'mindestensEinHandy', label: 'Es gibt jeweils mindestens 1 Handy mit Empfang und Akku.' },
            { key: 'echososApp', label: 'Die Leitenden haben die App "EchoSOS" auf ihrem Handy installiert.' },
            { key: 'notwendigkeitEntscheidung', label: 'Ueber die Notwendigkeit einer Alarmierung entscheidet das Leitungsteam.' },
            { key: 'spezifischeSituationen', label: 'Bei folgenden Situationen ist eine Alarmierung Pflicht:' },
            { key: 'personNichtSelbststaendig', label: 'Eine Person kann sich nicht selbststaendig bewegen.' },
            { key: 'bewusstlos', label: 'Eine Person ist oder war bewusstlos.' },
            { key: 'stundeVermisst', label: 'Eine Person wird waehrend mindestens einer Stunde vermisst.' },
            { key: 'feuerAusserKontrolle', label: 'Ein Feuer ist nicht mehr kontrollierbar.' },
            { key: 'blaulichtInfo', label: 'Nach dem Hinzuziehen einer Blaulichtorganisation muessen folgende Personen/Stellen informiert werden:' },
            { key: 'eltern', label: 'Eltern' },
            { key: 'abteilungsleitung', label: 'Abteilungsleitung' },
            { key: 'coach', label: 'Coach' },
            { key: 'badNews', label: 'Bad News / Pfadi-Helpline' },
            { key: 'leitendeNummern', label: 'Die Leitenden muessen folgende Nummer kennen.' },
            { key: 'leitendeHandyNummern', label: 'Die Leitenden muessen folgende Nummer auf ihrem Handy hinterlegt haben.' },
          ].map(rule => (
            <Form.Group key={rule.key} className="mb-2">
              <Form.Check
                type="checkbox"
                label={rule.label}
                checked={sikoData.alarmierung[rule.key] || false}
                onChange={() => handleCheckboxChange('alarmierung', rule.key)}
              />
            </Form.Group>
          ))}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Notfallauto vorhanden"
              checked={sikoData.notfallauto}
              onChange={() =>
                setSikoData(prev => ({ ...prev, notfallauto: !prev.notfallauto }))
              }
            />
          </Form.Group>
        </Form>

        <h5>QR-Code fuer Google Maps</h5>
        {sikoData.lagerAdresse && (
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <QRCodeCanvas
              value={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                sikoData.lagerAdresse
              )}`}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Schliessen
        </Button>
        <Button variant="primary" onClick={handleDownloadPdf}>
          PDF Herunterladen
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Sicherheitskonzept;